/** Un chip è "restituito" se ha una data di restituzione. */
export type ReturnableChip = {
  returnedAt: string | Date | null;
  /** Se valorizzato, il chip è stato rimosso dall'elenco. */
  removedAt?: string | Date | null;
};

export type Totals = {
  total: number;
  returned: number;
  missing: number;
  /** Frazione da 0 a 1 (non arrotondata: la formattazione è compito della UI). */
  percent: number;
};

export function isReturned(chip: ReturnableChip): boolean {
  return chip.returnedAt !== null && chip.returnedAt !== undefined;
}

/**
 * Un chip rimosso non fa parte dell'elenco: è archiviato.
 *
 * Il filtro vero è nelle query del server, che non lo restituisce nemmeno. Qui
 * c'è la seconda rete: se un chip rimosso arrivasse comunque a un totale — per
 * una risposta vecchia rimasta in volo, o per un selettore che dimentica di
 * filtrarlo — il conteggio non deve includerlo. Un totale sbagliato durante una
 * gara è peggio di un totale mancante.
 */
export function isRemoved(chip: ReturnableChip): boolean {
  return chip.removedAt !== null && chip.removedAt !== undefined;
}

export function computeTotals(chips: readonly ReturnableChip[]): Totals {
  let returned = 0;
  let total = 0;
  for (const chip of chips) {
    if (isRemoved(chip)) continue;
    total += 1;
    if (isReturned(chip)) returned += 1;
  }
  return {
    total,
    returned,
    missing: total - returned,
    percent: total === 0 ? 0 : returned / total,
  };
}

/** Etichetta usata per i chip senza campo `race` valorizzato. */
export const NO_RACE_LABEL = 'Senza gara';

/** Etichetta usata per i chip senza campo `team` valorizzato. */
export const NO_TEAM_LABEL = 'Senza squadra';

/**
 * L'etichetta di un gruppo: il valore, oppure la sua etichetta di ripiego.
 *
 * Una sola funzione per `race` e per `team`, perché chi raggruppa e chi mostra
 * il dettaglio devono etichettare **allo stesso modo**: se le due
 * normalizzazioni divergessero, aprire una squadra mostrerebbe l'elenco di
 * un'altra, e non ci sarebbe modo di accorgersene guardando lo schermo.
 */
function groupLabel(value: string | null | undefined, fallback: string): string {
  return value && value.trim() !== '' ? value : fallback;
}

export type RaceSummary = {
  race: string;
  total: number;
  returned: number;
  missing: number;
  percent: number;
};

export type RaceGroupableChip = ReturnableChip & { race: string | null };

/**
 * Avanzamento raggruppato per gara.
 *
 * L'ordinamento è alfabetico; i chip senza gara finiscono in fondo, così la
 * coda "Senza gara" non spezza l'elenco delle gare vere.
 */
export function computeRaceSummaries(chips: readonly RaceGroupableChip[]): RaceSummary[] {
  const groups = new Map<string, { total: number; returned: number }>();

  for (const chip of chips) {
    if (isRemoved(chip)) continue;
    const race = groupLabel(chip.race, NO_RACE_LABEL);
    const group = groups.get(race) ?? { total: 0, returned: 0 };
    group.total += 1;
    if (isReturned(chip)) group.returned += 1;
    groups.set(race, group);
  }

  return [...groups.entries()]
    .map(([race, group]) => ({
      race,
      total: group.total,
      returned: group.returned,
      missing: group.total - group.returned,
      percent: group.total === 0 ? 0 : group.returned / group.total,
    }))
    .sort((a, b) => {
      if (a.race === NO_RACE_LABEL) return 1;
      if (b.race === NO_RACE_LABEL) return -1;
      return a.race.localeCompare(b.race, 'it');
    });
}

export type TeamSummary = {
  team: string;
  total: number;
  returned: number;
  missing: number;
  percent: number;
};

export type TeamGroupableChip = ReturnableChip & { team: string | null };

/**
 * Avanzamento raggruppato per squadra.
 *
 * Gemello di `computeRaceSummaries`, e non una sua generalizzazione: `team` e
 * `race` sono due raggruppamenti diversi per significato — uno organizzativo,
 * l'altro sportivo — e potrebbero separarsi domani (una squadra potrebbe
 * diventare un'anagrafica con un ordinamento suo). Due funzioni corte e uguali
 * si leggono meglio di una sola con un selettore di campo, e si verificano da
 * sole. L'ordinamento è alfabetico, e i chip senza squadra finiscono in fondo
 * per lo stesso motivo per cui le gare senza nome ci finiscono.
 */
export function computeTeamSummaries(chips: readonly TeamGroupableChip[]): TeamSummary[] {
  const groups = new Map<string, { total: number; returned: number }>();

  for (const chip of chips) {
    if (isRemoved(chip)) continue;
    const team = groupLabel(chip.team, NO_TEAM_LABEL);
    const group = groups.get(team) ?? { total: 0, returned: 0 };
    group.total += 1;
    if (isReturned(chip)) group.returned += 1;
    groups.set(team, group);
  }

  return [...groups.entries()]
    .map(([team, group]) => ({
      team,
      total: group.total,
      returned: group.returned,
      missing: group.total - group.returned,
      percent: group.total === 0 ? 0 : group.returned / group.total,
    }))
    .sort((a, b) => {
      if (a.team === NO_TEAM_LABEL) return 1;
      if (b.team === NO_TEAM_LABEL) return -1;
      return a.team.localeCompare(b.team, 'it');
    });
}

export type TeamDetailChip = TeamGroupableChip & {
  firstName: string | null;
  lastName: string | null;
  code: string;
};

/**
 * I chip ancora mancanti, divisi per l'etichetta di squadra con cui si raggruppa.
 *
 * Le chiavi escono dalla **stessa** `groupLabel` di `computeTeamSummaries`, e
 * non è un dettaglio: è l'unico modo perché aprire una squadra dalla dashboard
 * mostri i suoi mancanti. Se le due normalizzazioni divergessero, il pannello
 * aperto sarebbe quello di un'altra squadra, senza modo di accorgersene.
 *
 * L'ordine è per cognome, poi nome, poi codice: è l'ordine in cui si cerca una
 * persona in un elenco da leggere ad alta voce al telefono.
 */
export function missingChipsByTeam<T extends TeamDetailChip>(
  chips: readonly T[],
): Map<string, T[]> {
  const groups = new Map<string, T[]>();

  for (const chip of chips) {
    if (isRemoved(chip) || isReturned(chip)) continue;
    const team = groupLabel(chip.team, NO_TEAM_LABEL);
    const list = groups.get(team);
    if (list) list.push(chip);
    else groups.set(team, [chip]);
  }

  for (const list of groups.values()) {
    list.sort(
      (a, b) =>
        (a.lastName ?? '').localeCompare(b.lastName ?? '', 'it') ||
        (a.firstName ?? '').localeCompare(b.firstName ?? '', 'it') ||
        a.code.localeCompare(b.code),
    );
  }

  return groups;
}

/**
 * Gli ultimi chip rientrati, dal più recente. Alimenta la lista "ultimi
 * restituiti" nella schermata evento.
 */
export function recentlyReturned<T extends ReturnableChip>(
  chips: readonly T[],
  limit = 8,
): T[] {
  return chips
    .filter((chip) => !isRemoved(chip) && isReturned(chip))
    .sort((a, b) => toTime(b.returnedAt) - toTime(a.returnedAt))
    .slice(0, limit);
}

/** Valori distinti e ordinati, per popolare i menu dei filtri. */
export function distinctSorted(
  values: Iterable<string | null | undefined>,
  options: { fallbackLabel?: string } = {},
): string[] {
  const set = new Set<string>();
  let hasEmpty = false;

  for (const value of values) {
    const trimmed = value?.trim();
    if (!trimmed) {
      hasEmpty = true;
      continue;
    }
    set.add(trimmed);
  }

  const sorted = [...set].sort((a, b) => a.localeCompare(b, 'it'));
  if (hasEmpty && options.fallbackLabel) sorted.push(options.fallbackLabel);
  return sorted;
}

function toTime(value: string | Date | null): number {
  if (value === null || value === undefined) return 0;
  const date = value instanceof Date ? value : new Date(value);
  const time = date.getTime();
  return Number.isNaN(time) ? 0 : time;
}
